import { AxiosError, isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { AXIOS_TIMEOUT_CODES } from 'constants/axios-error-codes';
import { REQUEST_ERROR_MESSAGE_KEY } from 'constants/error.constant';
import { HTTP_STATS_CODE } from 'constants/general';
import { t } from 'services/i18n.service';

export type RequestErrorMessageOptions = {
  fallbackKey?: string;
  timeoutKey?: string;
};

export const isAxiosNetworkError = (error: unknown): boolean =>
  isAxiosError(error) && error.response == null;

export const isAxiosTimeoutError = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;
  return !!axiosError?.code && AXIOS_TIMEOUT_CODES.includes(axiosError.code);
};

export const isTransientRequestError = (error: unknown): boolean =>
  isAxiosNetworkError(error) || isAxiosTimeoutError(error);

/** Authenticated but not allowed — must not trigger session logout (unlike 401). */
export const isForbiddenError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === HTTP_STATS_CODE.FORBIDDEN;

export const isTransientErrorPayload = (
  payload: unknown
): payload is { transient: true } =>
  typeof payload === 'object' &&
  payload !== null &&
  'transient' in payload &&
  (payload as { transient?: boolean }).transient === true;

export const getRequestErrorMessageKey = (
  error: unknown,
  {
    fallbackKey = REQUEST_ERROR_MESSAGE_KEY.DEFAULT,
    timeoutKey
  }: RequestErrorMessageOptions = {}
): string => {
  if (timeoutKey && isAxiosTimeoutError(error)) {
    return timeoutKey;
  }

  if (isTransientRequestError(error)) {
    return REQUEST_ERROR_MESSAGE_KEY.NETWORK;
  }

  return fallbackKey;
};

export const toastRequestError = (
  error: unknown,
  options: RequestErrorMessageOptions = {}
): boolean => {
  toast.error(t(getRequestErrorMessageKey(error, options)));
  return isTransientRequestError(error);
};

/** Non-403 request failures only — 403 is handled by the global Forbidden redirect. */
export const toastApiRequestError = (error: unknown): void => {
  if (isForbiddenError(error)) {
    return;
  }
  toast.error(t(REQUEST_ERROR_MESSAGE_KEY.DEFAULT));
};
